/**
 * WordPress User Service
 * Service gọi WordPress REST API để quản lý users
 */

import { User, UserProfile } from '../../types/auth';
import { normalizeVatliUrl } from '../../utils/urlNormalizer';
import { API_CONFIG } from '../../config/api.config';

class WordPressUserService {
  private apiUrl: string;
  private token: string | null = null;

  // ✅ OPTIMIZATION: Request deduplication - prevent duplicate in-flight requests
  private pendingRequests: Map<string, Promise<any>> = new Map();

  // ✅ OPTIMIZATION: Memory cache - cache user data for 1 minute
  private userCache: Map<string, { data: User; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 60000; // 1 minute in milliseconds

  constructor() {
    // ✅ Use centralized API configuration
    // WordPress được cài trên DOMAIN CHÍNH (vatli365.vn), không phải subdomain
    const customApiUrl = import.meta.env.VITE_WP_API_URL;

    if (customApiUrl) {
      // Custom URL from environment (được chuẩn hóa về vatli365.vn nếu nhập sai domain)
      this.apiUrl = normalizeVatliUrl(customApiUrl, API_CONFIG.wordpress.apiUrl);
    } else {
      // ✅ Use config default (vatli365.vn)
      // Cả Dev và Production đều dùng cùng WordPress backend
      // React app (localhost:5173, ebook.vatli365.vn, hoặc Vercel) gọi API từ vatli365.vn qua CORS
      this.apiUrl = API_CONFIG.wordpress.apiUrl;
    }

    console.log('🔗 WordPress API URL:', this.apiUrl);
    console.log('🔗 Mode:', import.meta.env.DEV ? 'Development (using Production API)' : 'Production');

    // Load token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Set authentication token
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Refresh token from localStorage (important for cross-browser/session)
   */
  private refreshTokenFromStorage(): void {
    const currentToken = localStorage.getItem('auth_token');
    // ✅ FIX: Update token even if it is null (logged out)
    if (currentToken !== this.token) {
      console.log('🔄 [WordPressUserService] Refreshing token from localStorage:', currentToken ? 'Token found' : 'Token cleared');
      this.token = currentToken;
    }
  }

  /**
   * Get authentication headers
   */
  private getHeaders(): HeadersInit {
    // Always refresh token from localStorage before making request
    this.refreshTokenFromStorage();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Handle API response with enhanced error handling
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    console.log('🔧 WordPress API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      ok: response.ok,
    });

    // Check Content-Type header
    const contentType = response.headers.get('content-type');
    console.log('📋 Response Content-Type:', contentType);

    if (!response.ok) {
      // Try to parse error as JSON first
      try {
        const error = await response.json();
        console.error('❌ API Error Response:', error);
        throw new Error(error.message || `API request failed with status ${response.status}`);
      } catch (jsonError) {
        // If not JSON, try to get text response
        try {
          const textResponse = await response.clone().text();
          console.error('❌ Response is not JSON. Text content:', textResponse.substring(0, 500));
          throw new Error(`API request failed: ${response.statusText}. Server returned non-JSON response.`);
        } catch (textError) {
          console.error('❌ Cannot read response:', textError);
          throw new Error(`API request failed: ${response.statusText}`);
        }
      }
    }

    // Response is OK, try to parse as JSON
    // Clone response first to allow reading it multiple times if needed
    const responseClone = response.clone();

    try {
      // Check if Content-Type is JSON
      if (contentType && !contentType.includes('application/json')) {
        console.warn('⚠️ Response Content-Type is not JSON:', contentType);
        const textResponse = await responseClone.text();
        console.error('❌ Response content:', textResponse.substring(0, 500));
        throw new Error('Server returned non-JSON response. This usually means the WordPress plugin is not activated or the API endpoint does not exist.');
      }

      // Try to get response text first for debugging
      const responseText = await responseClone.text();
      console.log('📄 Response text length:', responseText.length);
      console.log('📄 Response preview:', responseText.substring(0, 200));

      if (!responseText || responseText.trim().length === 0) {
        console.warn('⚠️ Response is empty (no JSON body)');
        return null as T;
      }

      // Parse JSON
      const data = JSON.parse(responseText);
      return data;
    } catch (parseError) {
      console.error('❌ Failed to parse JSON response:', parseError);

      // Try to get response text for debugging
      try {
        const textResponse = await response.text();
        console.error('❌ Full response content:', textResponse);
      } catch (e) {
        console.error('❌ Unable to read response:', e);
      }

      throw new Error('Đăng nhập thất bại: Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng và thử lại.');
    }
  }

  /**
   * Get all users (Admin only)
   */
  async getAllUsers(): Promise<User[]> {
    try {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📋 [WordPressUserService] getAllUsers()');
      console.log('🔗 API URL:', `${this.apiUrl}/users`);

      // Check if token exists in localStorage
      const currentToken = localStorage.getItem('auth_token');
      console.log('🔑 Token in localStorage:', !!currentToken);
      if (!currentToken) {
        console.error('❌ NO TOKEN IN LOCALSTORAGE!');
        console.error('💡 User needs to login to access admin functions.');
        throw new Error('Authentication required. Please login again.');
      }

      // getHeaders() will auto-refresh token from localStorage
      const headers = this.getHeaders();
      console.log('📤 Authorization header:', headers['Authorization'] ? 'Present ✅' : 'Missing ❌');

      const response = await fetch(`${this.apiUrl}/users`, {
        method: 'GET',
        headers: headers,
      });

      console.log('📥 Response status:', response.status);

      // Check for auth errors
      if (response.status === 401) {
        console.error('❌ 401 Unauthorized - Token invalid or expired');
        throw new Error('Your session has expired. Please login again.');
      }
      if (response.status === 403) {
        console.error('❌ 403 Forbidden - Need admin permission');
        throw new Error('Admin permission required to view users.');
      }

      const users = await this.handleResponse<any[]>(response);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ LOADED', users.length, 'USERS FROM WORDPRESS');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return users.map(this.formatUser);
    } catch (error) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ERROR GETTING ALL USERS:', error);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      throw error;
    }
  }

  /**
   * Get user by ID
   * ✅ OPTIMIZED: With request deduplication and memory cache
   */
  async getUserById(id: string): Promise<User | null> {
    const cacheKey = `user_${id}`;

    // ✅ Check memory cache first
    const cached = this.userCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log('✅ [Cache Hit] Returning cached user:', id);
      return cached.data;
    }

    // ✅ Check if request is already in-flight
    if (this.pendingRequests.has(cacheKey)) {
      console.log('🔄 [Dedup] Reusing in-flight request for user:', id);
      return this.pendingRequests.get(cacheKey)!;
    }

    // ✅ Create new request
    const promise = this._fetchUserById(id);
    this.pendingRequests.set(cacheKey, promise);

    try {
      const user = await promise;

      // ✅ Cache the result
      if (user) {
        this.userCache.set(cacheKey, { data: user, timestamp: Date.now() });
      }

      return user;
    } finally {
      // ✅ Clean up pending request
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Internal method to fetch user by ID (no caching)
   */
  private async _fetchUserById(id: string): Promise<User | null> {
    try {
      // ✅ Check if token exists before making request
      if (!this.token) {
        const tokenFromStorage = localStorage.getItem('auth_token');
        if (!tokenFromStorage) {
          // Silently return null if no token - don't log error for unauthenticated requests
          return null;
        }
        this.token = tokenFromStorage;
      }

      const response = await fetch(`${this.apiUrl}/users/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      // ✅ Handle 401 gracefully - token may be expired or invalid
      if (response.status === 401) {
        console.warn(`⚠️ [getUserById] Authentication required for user ${id}`);
        return null;
      }

      if (response.status === 404) {
        return null;
      }

      const user = await this.handleResponse<any>(response);
      return this.formatUser(user);
    } catch (error: any) {
      // ✅ Only log non-401 errors
      if (!error.message?.includes('401') && !error.message?.includes('Unauthorized')) {
        console.error('Error getting user by ID:', error);
      }
      return null;
    }
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const url = `${this.apiUrl}/users/email/${encodeURIComponent(email)}`;
      console.log('🔧 WordPress API Call - getUserByEmail:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (response.status === 404) {
        console.log('⚠️ User not found:', email);
      } else {
        const user = await this.handleResponse<any>(response);

        if (user) {
          console.log('✅ User found:', { id: user.id, email: user.email, role: user.role });
          return this.formatUser(user);
        }
      }
    } catch (error) {
      console.error('Error getting user by email:', error);
    }

    // Fallback: try search endpoint
    try {
      const searchUrl = `${this.apiUrl}/users?search=${encodeURIComponent(email)}`;
      console.log('🔍 Fallback search users:', searchUrl);
      const searchResponse = await fetch(searchUrl, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!searchResponse.ok) {
        console.warn('⚠️ Fallback search failed with status:', searchResponse.status);
        return null;
      }

      const users = await this.handleResponse<any[]>(searchResponse);
      if (Array.isArray(users)) {
        const matched = users.find(
          (u) => (u.email || '').toLowerCase() === email.toLowerCase()
        );
        if (matched) {
          console.log('✅ User found via fallback search:', matched.email);
          return this.formatUser(matched);
        }
      }
    } catch (searchError) {
      console.error('❌ Fallback search failed:', searchError);
    }

    console.log('⚠️ User not found after all attempts:', email);
    return null;
  }

  /**
   * Create new user
   */
  async createUser(userData: Partial<User>): Promise<User> {
    try {
      const response = await fetch(`${this.apiUrl}/users`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          email: userData.email,
          name: userData.name,
          avatar: userData.avatar,
          provider: userData.provider || 'google',
          providerId: userData.providerId,
          role: userData.role,
          permissions: userData.permissions,
          isVerified: userData.isVerified,
        }),
      });

      // ✅ Handle 409 Conflict (user already exists)
      if (response.status === 409) {
        console.log('⚠️ User already exists (409):', userData.email);

        // Try to parse 409 response body - it might contain user data
        try {
          const responseData = await response.json();
          console.log('📄 409 Response data:', responseData);

          // If response contains user data, use it
          if (responseData && responseData.id) {
            console.log('✅ Got user data from 409 response');
            return this.formatUser(responseData);
          }
        } catch (e) {
          console.warn('⚠️ Could not parse 409 response as JSON');
        }

        // Fallback: Try to fetch existing user
        try {
          const existingUser = await this.getUserByEmail(userData.email!);
          if (existingUser) {
            console.log('✅ Fetched existing user from WordPress:', existingUser);
            return existingUser;
          }
        } catch (fetchError) {
          console.error('❌ Cannot fetch existing user:', fetchError);
        }

        // Last resort: Return a minimal user object to allow login
        console.warn('⚠️ Returning minimal user object for existing user');
        return {
          id: `temp-${Date.now()}`,
          email: userData.email!,
          name: userData.name!,
          avatar: userData.avatar,
          provider: 'google' as const,
          providerId: userData.providerId,
          role: 'student' as const,
          permissions: [],
          isActive: true,
          isVerified: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }

      const user = await this.handleResponse<any>(response);
      console.log('✅ User created in WordPress database:', user);
      return this.formatUser(user);
    } catch (error) {
      console.error('❌ Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update user
   */
  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    try {
      // ✅ Log studentInfo với teacherEmail để debug
      if (updates.studentInfo) {
        console.log('📤 [WordPressUserService] Updating user with studentInfo:', {
          hasTeacherEmail: !!(updates.studentInfo as any)?.teacherEmail,
          teacherEmail: (updates.studentInfo as any)?.teacherEmail,
          class: (updates.studentInfo as any)?.class,
          school: (updates.studentInfo as any)?.school
        });
      }

      const response = await fetch(`${this.apiUrl}/users/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({
          name: updates.name,
          email: updates.email,
          role: updates.role,
          permissions: updates.permissions,
          isActive: updates.isActive,
          isVerified: updates.isVerified,
          studentInfo: updates.studentInfo,
          teacherInfo: updates.teacherInfo,
          avatar: updates.avatar,
          lastLoginAt: updates.lastLoginAt,
        }),
      });

      const user = await this.handleResponse<any>(response);
      console.log('✅ [WordPressUserService] User updated in WordPress database');

      // ✅ Log kết quả studentInfo sau khi update
      if (user.studentInfo || user.student_info) {
        const studentInfo = user.studentInfo || user.student_info;
        console.log('📥 [WordPressUserService] Updated studentInfo:', {
          hasTeacherEmail: !!studentInfo?.teacherEmail,
          teacherEmail: studentInfo?.teacherEmail,
          class: studentInfo?.class,
          school: studentInfo?.school
        });
      }

      return this.formatUser(user);
    } catch (error) {
      console.error('❌ [WordPressUserService] Error updating user:', error);
      throw error;
    }
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/users/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      await this.handleResponse<any>(response);
      console.log('✅ User deleted from WordPress database');
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Format user from API response
   */
  private formatUser(data: any): User {
    // ✅ Safe JSON parse helper
    const safeJsonParse = (value: any, fieldName: string): any => {
      // If already an object (not string), return as is
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
      }

      // If null, undefined, or empty string, return undefined
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return undefined;
      }

      // If string, try to parse JSON
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          // Only return if parsed result is an object (not array, not primitive)
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
          } else {
            console.warn(`⚠️ [formatUser] ${fieldName} parsed but is not an object:`, parsed);
            return undefined;
          }
        } catch (e) {
          console.warn(`⚠️ [formatUser] Failed to parse ${fieldName} string:`, e);
          return undefined;
        }
      }

      // If array or other type, return undefined
      return undefined;
    };

    // Parse studentInfo and teacherInfo
    const studentInfo = safeJsonParse(data.studentInfo || data.student_info, 'studentInfo');
    const teacherInfo = safeJsonParse(data.teacherInfo || data.teacher_info, 'teacherInfo');

    const formattedUser = {
      id: data.id,
      email: data.email,
      name: data.name,
      avatar: data.avatar || undefined,
      provider: data.provider as 'google' | 'email',
      providerId: data.providerId,
      role: data.role as 'student' | 'teacher' | 'admin',
      permissions: Array.isArray(data.permissions) ? data.permissions : [],
      isActive: data.isActive,
      isVerified: data.isVerified,
      teacherInfo: teacherInfo,
      studentInfo: studentInfo,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      lastLoginAt: data.lastLoginAt ? new Date(data.lastLoginAt) : undefined,
    };

    // Log để debug
    if (formattedUser.role === 'student') {
      console.log('🔍 [formatUser] Student info:', {
        hasStudentInfo: !!formattedUser.studentInfo,
        isObject: formattedUser.studentInfo && typeof formattedUser.studentInfo === 'object',
        keys: formattedUser.studentInfo ? Object.keys(formattedUser.studentInfo) : []
      });
    }

    if (formattedUser.role === 'teacher') {
      console.log('🔍 [formatUser] Teacher info:', {
        hasTeacherInfo: !!formattedUser.teacherInfo,
        isObject: formattedUser.teacherInfo && typeof formattedUser.teacherInfo === 'object',
        keys: formattedUser.teacherInfo ? Object.keys(formattedUser.teacherInfo) : []
      });
    }

    return formattedUser;
  }

  /**
   * User profile methods (placeholder - can be expanded)
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    // TODO: Implement profile endpoints if needed
    return null;
  }

  async createUserProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    // TODO: Implement profile endpoints if needed
    throw new Error('Not implemented yet');
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    // TODO: Implement profile endpoints if needed
    throw new Error('Not implemented yet');
  }

  /**
   * Check WordPress API health
   */
  async checkHealth(): Promise<{
    isHealthy: boolean;
    responseTime: number;
    hasTables: boolean;
    error?: string;
  }> {
    const startTime = Date.now();

    try {
      // Try to fetch users endpoint
      const response = await fetch(`${this.apiUrl}/users`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      const responseTime = Date.now() - startTime;

      // Even 401 means API is working (just needs auth)
      if (response.status === 401 || response.status === 200) {
        return {
          isHealthy: true,
          responseTime,
          hasTables: true,
        };
      }

      // 404 means plugin not installed/activated
      if (response.status === 404) {
        return {
          isHealthy: false,
          responseTime,
          hasTables: false,
          error: 'WordPress plugin not activated',
        };
      }

      // Other errors
      return {
        isHealthy: false,
        responseTime,
        hasTables: false,
        error: `API returned status ${response.status}`,
      };
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      return {
        isHealthy: false,
        responseTime,
        hasTables: false,
        error: error.message || 'Network error',
      };
    }
  }
}

export const wordPressUserService = new WordPressUserService();
export default wordPressUserService;

